'use client';
import { useLocale } from 'next-intl';
import type React from 'react';
import { useEffect, useCallback } from 'react';

const PageLeaveTracker: React.FC<{
    onPageUnload?: (e: BeforeUnloadEvent) => string | null;
    onNavigate?: (href: string) => void | boolean | Promise<boolean>;
}> = ({ onPageUnload, onNavigate }) => {
    const locale = useLocale();
    const handleLinkClick = useCallback(
        (e) => {
            const link = e.target.closest('a');
            if (!link?.href || link.target.includes('_blank')) {
                return;
            }

            const url = new URL(link.href);
            const targetPath = url.pathname.replace(`/${locale}`, '') + url.search + url.hash;

            if (window.location.href === link.href) {
                return;
            }

            const isNavigationForbidden = onNavigate?.(targetPath);

            if (isNavigationForbidden) {
                e.preventDefault();
                e.stopPropagation();
            }
        },
        [locale, onNavigate]
    );

    useEffect(() => {
        document.addEventListener('click', handleLinkClick, { capture: true });
        window.addEventListener('beforeunload', onPageUnload);

        return () => {
            document.removeEventListener('click', handleLinkClick, { capture: true });
            window.removeEventListener('beforeunload', onPageUnload);
        };
    }, [handleLinkClick, onPageUnload]);

    return null;
};

export default PageLeaveTracker;
